import type { QueryClient, QueryKey } from '@tanstack/react-query';
import { parseFlowsString } from 'utils';
import type { AdSetEntry } from '../api/getAdSets';
import queryKeys from '../api/queryKeys';

export const isAdSetFlowEmpty = (flow: string | null | undefined): boolean =>
  flow == null || flow === '';

export const collectUniqueAdSetFlows = (
  adSets: readonly { flow?: string | null }[],
): string[] => [...new Set(adSets.flatMap((adSet) => parseFlowsString(adSet.flow)))];

export const expandAdSetsByFlow = <T extends { flow?: string | null }>(
  entries: T[],
): T[] =>
  entries.flatMap((entry) => {
    const flows = parseFlowsString(entry.flow);
    if (flows.length <= 1) {
      return [entry];
    }
    return flows.map((flow) => ({ ...entry, flow }));
  });

export const updateAdSetFlowsInCache = (
  client: QueryClient,
  flowsByAdSetId: Record<string, string>,
): void => {
  const matches = client.getQueriesData<AdSetEntry[]>({
    queryKey: queryKeys.adSets,
  });

  for (const [queryKey, data] of matches) {
    if (!data) continue;

    client.setQueryData<AdSetEntry[]>(
      queryKey,
      data.map((adSet) =>
        Object.hasOwn(flowsByAdSetId, adSet.id)
          ? { ...adSet, flow: flowsByAdSetId[adSet.id], flowAssignedByUser: true }
          : adSet,
      ),
    );
  }
};

export const updateAdSetFlowInCache = (
  client: QueryClient,
  adSetId: string,
  flow: string,
): void => {
  updateAdSetFlowsInCache(client, { [adSetId]: flow });
};

interface CachedAdSetFlow {
  flow: string;
  flowAssignedByUser?: boolean;
}

export const mergeCachedAdSetFlows = (
  fresh: AdSetEntry[],
  existing: AdSetEntry[] | undefined,
): AdSetEntry[] => {
  if (!existing?.length) {
    return fresh;
  }

  const flowById = new Map<string, CachedAdSetFlow>(
    existing
      .filter((adSet) => !isAdSetFlowEmpty(adSet.flow))
      .map((adSet) => [
        adSet.id,
        {
          flow: adSet.flow!,
          flowAssignedByUser: adSet.flowAssignedByUser,
        },
      ]),
  );

  if (flowById.size === 0) {
    return fresh;
  }

  return fresh.map((adSet) => {
    if (!isAdSetFlowEmpty(adSet.flow)) {
      return adSet;
    }

    const cached = flowById.get(adSet.id);
    if (!cached) {
      return adSet;
    }

    return {
      ...adSet,
      flow: cached.flow,
      flowAssignedByUser: cached.flowAssignedByUser,
    };
  });
};

export const mergeCachedAdSetFlowsFromClient = (
  client: QueryClient,
  fresh: AdSetEntry[],
  queryKey: QueryKey,
): AdSetEntry[] =>
  mergeCachedAdSetFlows(fresh, client.getQueryData<AdSetEntry[]>(queryKey));
